import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Mail, Lock, UserPlus, LogIn, User } from 'lucide-react';
import { useSupabaseAuth } from '@/hooks/useSupabaseAuth';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
interface SupabaseAuthSectionProps {
  onSuccess: () => void;
}
export const SupabaseAuthSection = ({
  onSuccess
}: SupabaseAuthSectionProps) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const {
    signIn,
    signUp
  } = useSupabaseAuth();
  const {
    toast
  } = useToast();
  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('يرجى إدخال البريد الإلكتروني وكلمة المرور');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const {
        error
      } = await signIn(email, password);
      if (error) {
        setError('بيانات الدخول غير صحيحة. تأكد من البريد الإلكتروني وكلمة المرور.');
        console.log('Sign in error:', error.message);
      } else {
        toast({
          title: "تم تسجيل الدخول بنجاح",
          description: `مرحباً ${email}`
        });
        onSuccess();
      }
    } catch (err) {
      console.error('Sign in error:', err);
      setError('حدث خطأ أثناء تسجيل الدخول');
    }
    setLoading(false);
  };
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || !fullName.trim()) {
      setError('يرجى ملء جميع الحقول المطلوبة');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // التحقق من وجود موظف بنفس البريد الإلكتروني
      const {
        data: existingEmployee
      } = await supabase.from('employees').select('approval_status, email').eq('email', email).maybeSingle();
      if (existingEmployee) {
        if (existingEmployee.approval_status === 'pending') {
          setError('تم إدخال هذه المعلومات سابقاً والحساب تحت المراجعة. يرجى انتظار الموافقة من المدير.');
          setLoading(false);
          return;
        } else if (existingEmployee.approval_status === 'rejected') {
          setError('تم رفض هذا الحساب مسبقاً. يرجى التواصل مع إدارة النظام.');
          setLoading(false);
          return;
        }
      }
      const {
        data,
        error
      } = await signUp(email, password, {
        data: {
          full_name: fullName.trim(),
          display_name: fullName.trim()
        }
      });
      if (error) {
        if (error.message.includes('already registered')) {
          setError('هذا البريد الإلكتروني مسجل مسبقاً. جرب تسجيل الدخول بدلاً من ذلك.');
        } else {
          setError('حدث خطأ أثناء إنشاء الحساب. تأكد من صحة البيانات.');
        }
        console.log('Sign up error:', error.message);
      } else {
        toast({
          title: "تم إرسال طلب التسجيل",
          description: "حسابك قيد المراجعة وسيتم إشعارك عند الموافقة عليه"
        });

        // عرض رسالة تأكيد
        setError('');
        setTimeout(() => {
          setError('تم إرسال طلب التسجيل بنجاح. حسابك الآن تحت المراجعة وسيتم التواصل معك عند الموافقة من قبل المدير.');
        }, 1000);
      }
    } catch (err) {
      console.error('Sign up error:', err);
      setError('حدث خطأ أثناء إنشاء الحساب');
    }
    setLoading(false);
  };
  return <div className="space-y-4">
      <Tabs defaultValue="signin" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="signin">تسجيل الدخول</TabsTrigger>
          <TabsTrigger value="signup">  إنشاء حساب جديد</TabsTrigger>
        </TabsList>

        <TabsContent value="signin">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LogIn className="w-5 h-5" />
                تسجيل الدخول
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSignIn} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signin-email">البريد الإلكتروني</Label>
                  <div className="relative">
                    <Mail className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                    <Input id="signin-email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="pr-10" placeholder="example@domain.com" required />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signin-password">كلمة المرور</Label>
                  <div className="relative">
                    <Lock className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                    <Input id="signin-password" type="password" value={password} onChange={e => setPassword(e.target.value)} className="pr-10" placeholder="أدخل كلمة المرور" required />
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'جاري تسجيل الدخول...' : 'تسجيل الدخول'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="signup">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                إنشاء حساب جديد
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-name">الاسم الكامل *</Label>
                  <div className="relative">
                    <User className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                    <Input id="signup-name" type="text" value={fullName} onChange={e => setFullName(e.target.value)} className="pr-10" placeholder="أدخل اسمك الكامل" required />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-email">البريد الإلكتروني *</Label>
                  <div className="relative">
                    <Mail className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                    <Input id="signup-email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="pr-10" placeholder="example@domain.com" required />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-password">كلمة المرور *</Label>
                  <div className="relative">
                    <Lock className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                    <Input id="signup-password" type="password" value={password} onChange={e => setPassword(e.target.value)} className="pr-10" placeholder="أدخل كلمة المرور (6 أحرف على الأقل)" minLength={6} required />
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'جاري إنشاء الحساب...' : 'إنشاء حساب'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {error && <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>}

    </div>;
};